import {
  createRouter,
  createWebHistory,
  type RouteRecordRaw,
} from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { useUserStore } from "@/stores/user";
import { useConfigStore } from "@/stores/config";
import i18n from "@/core/plugins/i18n";

const { t } = i18n.global

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    redirect: "/buckets",
    component: () => import("@/layouts/main-layout/MainLayout.vue"),
    meta: {
      middleware: "auth",
    },
    children: [
      /*
      {
        path: "/dashboard",
        name: "dashboard",
        component: () => import("@/views/Dashboard.vue"),
        meta: {
          pageTitle: t("pages.dashboard"),
        },
      },
      */


      /* Storage Policies */
      {
        path: "/storage_policy",
        name: "storage_policies_list",
        meta: {
          pageTitle: "Storage Policies",
          pageDescription: "page_descriptions.storage_policies",
        },
        component: () => import("@/views/storage_policy/StoragePolicyList.vue")
      },
      {
        path: "/storage_policy/edit/:sp_key",
        name: "storage_policy_edit",
        meta: {
          pageTitle: t("pages.storage_policy_edit"),
          pageDescription: "page_descriptions.storage_policy_edit",
        },
        component: () => import("@/views/storage_policy/StoragePolicyWizardCard.vue")
      },
      {
        path: "/storage_policy/new",
        name: "storage_policy_wizard",
        meta: {
          pageTitle: t("pages.storage_policy_create"),
          pageDescription: "page_descriptions.storage_policy_create",
        },
        component: () => import("@/views/storage_policy/StoragePolicyWizardCard.vue")
      },

      {
        path: "/storage_policy/new",
        name: "storage_policy_wizard",
        meta: {
          pageTitle: t("pages.storage_policy_create"),
          pageDescription: "page_descriptions.storage_policy_create",
        },
        component: () => import("@/views/storage_policy/StoragePolicyWizardCard.vue")
      },

      {
        path: "/location-catalog",
        meta: {
          pageTitle: "Location Catalog",
          pageDescription: "page_descriptions.location_catalog",
        },
        component: () => import("@/views/location-catalog/LocationCatalog.vue"),
        children: [
          {
            path: '',
            name: 'location-catalog',
            redirect: { name: 'location-catalog-locations' }
          },
          {
            path: "locations",
            name: "location-catalog-locations",
            component: () => import("@/views/location-catalog/LocationCatalogLocations.vue"),
          },
          {
            path: "carbon-emissions",
            name: "location-catalog-carbon",
            component: () => import("@/views/location-catalog/LocationCatalogCarbon.vue"),
          },
        ],
      },

      {
        path: "/storage_policy/locations",
        name: "storage_locations",
        meta: {
          pageTitle: t("pages.storage_locations"),
        },
        redirect: { name: "location-catalog-locations" },
      },

      /* Access */
      {
        path: "/agents",
        name: "agents",
        meta: {
          pageTitle: "Agent Connections",
          pageDescription: "page_descriptions.agents",
        },
        component: () => import("@/views/access/Agents.vue")
      },
      {
        path: "/agents/new",
        name: "agents-create",
        meta: {
          pageTitle: "Connect an Agent",
          pageDescription: "page_descriptions.agent_create",
        },
        component: () => import("@/views/access/CreateAgent.vue")
      },
      {
        path: "/agents/:agent_id",
        meta: {
          pageTitle: "Agent Connection Details",
        },
        component: () => import("@/views/access/AgentDetails.vue"),
        children: [
          {
            path: "",
            name: "agent-details",
            redirect: { name: "agent-summary" },
          },
          {
            path: "summary",
            name: "agent-summary",
            component: () => import("@/views/access/agent-details/Summary.vue"),
          },
          {
            path: "grants/:policy_uuid?",
            name: "agent-grants",
            component: () => import("@/views/access/agent-details/Grants.vue"),
          },
        ],
      },
      {
        path: "/api_keys",
        name: "apikeys-list",
        meta: {
          pageTitle: "S3 Access Keys",
          pageDescription: "page_descriptions.api_keys",
        },
        component: () => import("@/views/api_key/ApiKeysList.vue")
      },
      {
        path: "/api_keys/new",
        name: "apikeys-create",
        meta: {
          pageTitle: "New API Key",
          pageDescription: "page_descriptions.api_key_create",
        },
        component: () => import("@/views/api_key/CreateApiKey.vue")
      },
      {
        path: "/access_policies",
        name: "access-policies",
        meta: {
          pageTitle: "Access Policies",
          pageDescription: "page_descriptions.access_policies",
        },
        component: () => import("@/views/access/AccessPolicies.vue")
      },


      /* Buckets */
      {
        path: "/buckets",
        name: "buckets-list",
        meta: {
          pageTitle: "Buckets",
          pageDescription: "page_descriptions.buckets",
        },
        component: () => import("@/views/bucket/BucketsList.vue")
      },
      {
        path: "/buckets/new",
        name: "bucket-create",
        meta: {
          pageTitle: "New Bucket",
          pageDescription: "page_descriptions.bucket_create",
        },
        component: () => import("@/views/bucket/BucketWizard.vue")
      },

      /* Bucket Details */
      {
        path: "/buckets/:bucket_name",
        meta: {
          pageTitle: "Bucket Details",
        },
        component: () => import("@/views/bucket/BucketDetails.vue"),
        children: [
          {
            path: '',
            name: 'bucket-details',
            redirect: { name: 'bucket-contents' }
          },
          {
            path: "objects",
            name: "bucket-contents",
            component: () => import("@/views/bucket/details/Objects.vue"),
          },
          {
            path: "policy",
            name: "bucket-storage-policy",
            component: () => import("@/views/bucket/details/Policy.vue"),
          },
          {
            path: "access-control",
            name: "bucket-access-control",
            component: () => import("@/views/bucket/details/AccessControl.vue"),
          },
          {
            path: "apikeys",
            name: "bucket-apikeys",
            component: () => import("@/views/bucket/details/ApiKeys.vue"),
          },
          {
            path: "cors",
            name: "bucket-cors",
            component: () => import("@/views/bucket/details/Cors.vue"),
          },
          {
            path: "config",
            name: "bucket-config",
            component: () => import("@/views/bucket/details/Configuration.vue"),
          },
          {
            path: "migrations/:migration_uuid?",
            name: "bucket-migrations",
            component: () => import("@/views/bucket/details/Migrations.vue"),
          },
          {
            path: "usage",
            name: "bucket-usage",
            component: () => import("@/views/bucket/details/BucketEvents.vue"),
          },
          {
            path: "audit-log",
            name: "bucket-audit-log",
            component: () => import("@/views/bucket/details/AuditLog.vue"),
          },

          {
            path: "adaptive-placement",
            name: "adaptive-placement",
            component: () => import("@/views/bucket/details/AdaptivePlacement.vue"),
          },



        ],
      },

      /* Profile: personal settings of the signed-in user */
      {
        path: "/profile",
        meta: {
          pageTitle: "Profile",
          pageDescription: "page_descriptions.profile_settings",
        },
        component: () => import("@/views/profile/ProfileSettings.vue"),
        children: [
          {
            path: '',
            name: 'profile-settings',
            redirect: { name: 'profile-settings-profile' }
          },
          {
            path: "profile",
            name: "profile-settings-profile",
            component: () => import("@/views/profile/details/Profile.vue"),
          },
          {
            path: "security",
            name: "profile-settings-security",
            component: () => import("@/views/profile/details/Security.vue"),
          },
        ],
      },
      /* Account: the account (internally "team") the user belongs to, the same for solo users and teams */
      {
        path: "/account",
        meta: {
          pageTitle: "Account",
          pageDescription: "page_descriptions.account_settings",
        },
        component: () => import("@/views/team-settings/TeamSettings.vue"),
        children: [
          // The first tab: Identity for admins, Users for everyone else (Identity is admin only)
          {
            path: "",
            name: "account",
            redirect: () => ({ name: useUserStore().user?.is_admin ? "account-identity" : "account-users" }),
          },
          {
            path: "quotas",
            name: "account-quotas",
            component: () => import("@/views/profile/details/Account.vue"),
          },
          {
            path: "users",
            name: "account-users",
            component: () => import("@/views/team-settings/details/Members.vue"),
          },
          {
            path: "invites",
            name: "account-invites",
            component: () => import("@/views/team-settings/details/PendingInvites.vue"),
          },
          {
            // Invite form of named accounts, opened by the "Invite people" buttons (no tab)
            path: "invite",
            name: "account-invite",
            component: () => import("@/views/team-settings/details/InviteMember.vue"),
          },
          {
            path: "identity",
            name: "account-identity",
            component: () => import("@/views/team-settings/details/EditTeamSettings.vue"),
          },
          // Previous URLs of the solo account pages
          { path: "overview", redirect: { name: "account-quotas" } },
          { path: "settings", redirect: { name: "account-identity" } },
          { path: "subscription", redirect: { name: "account-quotas" } },
          { path: "members", redirect: { name: "account-users" } },
          { path: "profile", redirect: { name: "profile-settings-profile" } },
          { path: "security", redirect: { name: "profile-settings-security" } },
        ],
      },
      /* Names a paid account that has no name yet, then invites people (the name appears in invitation emails) */
      {
        path: "/team-setup",
        name: "team-setup",
        meta: {
          pageTitle: "Invite People",
          pageDescription: "page_descriptions.team_setup",
        },
        component: () => import("@/views/TeamSetupWizard.vue"),
      },
      { path: "/team-upgrade", redirect: { name: "team-setup" } },
      /* Upgrades a free tier account to a paid tier */
      {
        path: "/upgrade",
        name: "upgrade",
        meta: {
          pageTitle: "Upgrade",
          pageDescription: "page_descriptions.upgrade",
        },
        component: () => import("@/views/UpgradeWizard.vue"),
      },
      /* Previous URLs of the team settings pages */
      { path: "/team-settings", redirect: { name: "account" } },
      { path: "/team-settings/details", redirect: { name: "account-identity" } },
      { path: "/team-settings/members", redirect: { name: "account-users" } },
      { path: "/team-settings/invites", redirect: { name: "account-invites" } },
      { path: "/team-settings/invite", redirect: { name: "account-invite" } },

      /* Docs */
      {
        path: "/docs",
        name: "documentation",
        meta: {
          pageTitle: "Documentation",
        },
        component: () => import("@/views/documentation/Docs.vue"),
        children: [
          {
            path: "quickstart",
            name: "docs-quickstart",
            component: () => import("@/views/documentation/pages/Quickstart.vue"),
          },
          {
            path: "features",
            name: "docs-features",
            component: () => import("@/views/documentation/pages/S3Features.vue"),
          },
          {
            path: "tools",
            name: "docs-tools",
            component: () => import("@/views/documentation/pages/Tools.vue"),
          },


        ],
      },

      /* Empyrean Associations */
      {
        path: "/associations",
        name: "associations-list",
        meta: {
          pageTitle: "Associations",
          pageDescription: "page_descriptions.associations",
        },
        component: () => import("@/views/associations/AssociationsList.vue")
      },
      {
        path: "/associations/new",
        name: "associations-new",
        meta: {
          pageTitle: "New Association",
          pageDescription: "page_descriptions.association_create",
        },
        component: () => import("@/views/associations/CreateAssociation.vue")
      },
      {
        path: "/associations/join",
        name: "associations-join",
        meta: {
          pageTitle: "Join Association",
          pageDescription: "page_descriptions.association_join",
        },
        component: () => import("@/views/associations/JoinAssociation.vue")
      },

      {
        path: "/associations/:association_id",
        meta: {
          pageTitle: "Association Details",
        },
        component: () => import("@/views/associations/AssociationDetails.vue"),
        children: [
          {
            path: '',
            name: 'association-details',
            redirect: { name: 'association-members' }
          },
          /*
          {
            path: "summary",
            name: "association-summary",
            component: () => import("@/views/associations/details/Summary.vue"),
          },
          */
          {
            path: "members",
            name: "association-members",
            component: () => import("@/views/associations/details/Members.vue"),
          },
          {
            path: "gateways",
            name: "association-gateways",
            component: () => import("@/views/associations/details/Gateways.vue"),
          },
          {
            path: "edge-devices",
            name: "association-edge-devices",
            component: () => import("@/views/associations/details/EdgeDevices.vue"),
          },
          {
            path: "storage-policies",
            name: "association-storage-policies",
            component: () => import("@/views/associations/details/StoragePolicies.vue"),
          },
          {
            path: "buckets",
            name: "association-buckets",
            component: () => import("@/views/associations/details/Buckets.vue"),
          },

        ]
        },
    ],
  },
  {
    path: "/",
    component: () => import("@/layouts/AuthLayout.vue"),
    children: [
      {
        path: "/login",
        name: "sign-in",
        component: () =>
          import("@/views/auth/SignIn.vue"),
        meta: {
          pageTitle: "Sign In",
        },
      },

      {
        path: "/sign-up",
        name: "sign-up",
        component: () =>
          import("@/views/auth/SignUp.vue"),
        meta: {
          pageTitle: "Sign Up",
        },
      },
      {
        path: "/join",
        alias: "/join-team",
        name: "join-team",
        component: () =>
          import("@/views/auth/Join.vue"),
        meta: {
          pageTitle: "Join Team",
        },
      },
      {
        path: "/password-reset",
        name: "password-reset",
        component: () =>
          import("@/views/auth/PasswordResetRequest.vue"),
        meta: {
          pageTitle: "Password reset",
        },
      },
      {
        path: "/reset-password/token=:token",
        name: "reset-password-token",
        component: () =>
          import("@/views/auth/ResetPassword.vue"),
        meta: {
          pageTitle: "Reset password",
        },
      },
      {
        path: "/reset-password",
        name: "reset-password",
        component: () =>
          import("@/views/auth/ResetPassword.vue"),
        meta: {
          pageTitle: "Reset password",
        },
      }
    ],
  },
  {
    path: "/oauth",
    component: () => import("@/layouts/OAuthConsentLayout.vue"),
    meta: {
      middleware: "auth",
    },
    children: [
      {
        path: "authorize",
        name: "oauth-authorize",
        component: () => import("@/views/oauth/Authorize.vue"),
        meta: {
          pageTitle: "Authorize Application",
        },
      },
    ],
  },

  {
    path: "/",
    component: () => import("@/layouts/SystemLayout.vue"),
    children: [
      {
        // the 404 route, when none of the above matches
        path: "/404",
        name: "404",
        component: () => import("@/views/error_pages/Error404.vue"),
        meta: {
          pageTitle: "Error 404",
        },
      },
      {
        path: "/500",
        name: "500",
        component: () => import("@/views/error_pages/Error500.vue"),
        meta: {
          pageTitle: "Error 500",
        },
      },
    ],
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: "/404",
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

router.beforeEach((to, from, next) => {
  const authStore = useAuthStore();
  const userStore = useUserStore();
  const configStore = useConfigStore();

  // Set page title
  document.title = `${to.meta.pageTitle} | ${import.meta.env.VITE_APP_NAME}`;

  // Reset config to initial state
  configStore.resetLayoutConfig();

  // Verify auth token on page reload, if the page is authenticated
  /*
  if(to.meta.middleware == "auth" && (!from || !from.matched || !from.matched.length)){
    authStore.verifyAuth().then(_ => {
      // Reload the cached user, if any
      // userStore.load_user()
    });
  }
  */

  // Verify auth if the page is protected
  if (to.meta.middleware == "auth" && !authStore.isAuthenticated) {
    // clear up the stored auth
    userStore.delete_cached_user()
    authStore.logout()
    next({ name: "sign-in", query: { redirect: to.fullPath } });
  }
  else {
    // OK, navigate to the requested destination
    next();
  }

  // Scroll page to top on every route change
  window.scrollTo({
    top: 0,
    left: 0,
    behavior: "smooth",
  });

  // Clear all tooltips
  document.querySelectorAll('.tooltip').forEach(el => el.remove())
});

export default router;
